import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { captureAnalytics } from '../../../actions/preload';
import COLORS from '../../../util/constants/colors';
import { SOURCE_META } from './SummaryCards';
import { DATE_FORMAT } from './helpers';

const TEAM_EMR_SERIES = [
  { key: 'charts', label: 'Charts created', color: COLORS.PURPLE_PRIMARY_LITE },
  { key: 'tasks', label: 'Tasks created', color: COLORS.PURPLE_SECONDARY_LITE },
  { key: 'sent_to_chart', label: 'Sent to chart', color: '#4f8a8b' },
  { key: 'insurance', label: 'Insurance updates', color: '#b9975b' },
];

// Stacked bars only where the series partition the work. Team EMR types overlap
// (a chart-and-task counts as both), so that view groups its bars instead.
const VIEWS = {
  emr_by_source: {
    label: 'EMR by source',
    subtitle: 'Team activity + organization-wide automation',
    stacked: true,
    series: (showArm) => ['team', 'arm', 'automation']
      .filter((key) => key !== 'arm' || showArm)
      .map((key) => ({
        key,
        label: SOURCE_META[key].label,
        color: SOURCE_META[key].color,
        value: (week) => week.emr[key],
      })),
  },
  emr_referrals: {
    label: 'EMR + referrals',
    subtitle: 'All EMR operations and referral status changes by your team',
    stacked: false,
    series: () => [
      {
        key: 'emr',
        label: 'EMR operations',
        color: COLORS.PURPLE_PRIMARY_LITE,
        value: (week) => week.emr.team,
      },
      {
        key: 'status_changes',
        label: 'Status changes',
        color: '#4f8a8b',
        value: (week) => week.status_changes,
      },
    ],
  },
  team_emr: {
    label: 'Team EMR details',
    subtitle: 'Your team only · an operation can count in more than one type',
    stacked: false,
    series: () => TEAM_EMR_SERIES.map((series) => ({ ...series, value: (week) => week.team_emr[series.key] })),
  },
};

const WorkOverTime = ({ weeks, showArm }) => {
  const [view, setView] = useState('emr_by_source');
  const { subtitle, stacked, series: buildSeries } = VIEWS[view];
  const series = buildSeries(showArm);
  const changeView = (nextView) => {
    if (nextView === view) return;

    captureAnalytics('user_activity_work_over_time_view_changed', { view: nextView });
    setView(nextView);
  };
  const data = weeks.map((week) => ({
    label: moment(week.week_start, DATE_FORMAT).format('MMM D'),
    ...Object.fromEntries(series.map((item) => [item.key, item.value(week)])),
  }));

  return (
    <div className="user-activity-card user-activity-work-over-time">
      <div className="user-activity-section-header">
        <div>
          <h3>Work over time</h3>
          <span className="user-activity-caption">{subtitle}</span>
        </div>
        <div className="user-activity-toggle">
          <span className="user-activity-caption">One bar per week</span>
          {Object.entries(VIEWS).map(([key, option]) => (
            <button
              key={key}
              type="button"
              className={key === view ? 'active' : ''}
              onClick={() => changeView(key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid vertical={false} stroke={COLORS.PURPLE_QUARTERNARY_LITE} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
          {/* Only the hovered bar or segment, not every series in its week. */}
          <Tooltip shared={false} cursor={false} />
          <Legend iconType="square" />
          {series.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              fill={item.color}
              stackId={stacked ? 'work' : undefined}
              maxBarSize={56}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

WorkOverTime.propTypes = {
  weeks: PropTypes.array.isRequired,
  showArm: PropTypes.bool.isRequired,
};

export default WorkOverTime;
